import type { IQuoteBlotterRequest } from 'apiConnector/blotters';
import type { LanguageIsoCode } from 'locales';
import type { IBlotterQuoteItem } from 'models/IBlotterItem';
import { createSelector } from 'reselect';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { AppState } from 'state/state.model';
import { getTodayStringInLocalTimezone, toEndOfDay, toStartOfDay } from 'utils/core/date';
import { now } from 'utils/core/now';
import { translateText } from 'utils/lang/lang';

import { getSortFromColumnDefinitions, toLocaleDate, toLocaleDateOnlyString } from '../common/blotter.utils';
import type {
  IQuoteBlotterFilters,
  IQuoteBlotterFiltersWithMandatoryDates,
  QuoteBlotterState,
} from './quoteBlotter.model';

export function getColumnDefinition(
  state: QuoteBlotterState,
): readonly IBlotterColumnDefinition<IBlotterQuoteItem>[] | undefined {
  return state.columnDefinition;
}

export const getFilters = createSelector(
  [
    (s: QuoteBlotterState) => s.filters.quickSearchTerm,
    (s: QuoteBlotterState) => s.filters.advancedFilters,
    (s: QuoteBlotterState) => s.filters.startDate,
    (s: QuoteBlotterState) => s.filters.endDate,
    (s: QuoteBlotterState) => s.filters.dateFilter,
    (s: QuoteBlotterState) => s.filters.activityFilter,
    (s: QuoteBlotterState) => s.filters.productFilter,
    (s: QuoteBlotterState) => s.filters.platformFilter,
    (s: QuoteBlotterState) => s.filters.currencyFilter,
    (s: QuoteBlotterState) => s.filters.deskFilter,
  ],
  (
    quickSearchTerm,
    advancedFilters,
    startDate,
    endDate,
    dateFilter,
    activityFilter,
    productFilter,
    platformFilter,
    currencyFilter,
    deskFilter,
  ): IQuoteBlotterFiltersWithMandatoryDates => {
    const custom = 'custom';
    const allPlatforms = 'allPlatforms';
    const allCurrencies = 'allCurrencies';
    return {
      quickSearchTerm: quickSearchTerm,
      dateFilter: advancedFilters && startDate ? custom : dateFilter,
      activityFilter: activityFilter,
      productFilter: productFilter,
      platformFilter: platformFilter || allPlatforms,
      advancedFilters: advancedFilters,
      currencyFilter: (advancedFilters && currencyFilter) || allCurrencies,
      startDate: (advancedFilters && startDate) || toLocaleDateOnlyString(toLocaleDate(dateFilter)),
      endDate: (advancedFilters && endDate) || toLocaleDateOnlyString(now()),
      deskFilter,
    };
  },
);

export function isEndDateInThePast(state: QuoteBlotterState): boolean {
  const { endDate } = getFilters(state);
  const today = getTodayStringInLocalTimezone();
  return new Date(today) > new Date(endDate);
}

export function getFiltersToPersist(state: QuoteBlotterState): IQuoteBlotterFilters {
  return state.filters;
}

export const getQuoteBlotterRequest = createSelector(
  [
    (s: QuoteBlotterState) => s.columnDefinition,
    (s: QuoteBlotterState) => s.page,
    (s: QuoteBlotterState) => s.sort,
    (s: QuoteBlotterState) => s.blotterPreferenceState.pageSizeState,
    getFilters,
    (_s: QuoteBlotterState, lang: LanguageIsoCode) => lang,
  ],
  (columnDefinition, page, sort, pageSize, filters, lang): IQuoteBlotterRequest => {
    const computedSort = getSortFromColumnDefinitions(columnDefinition);
    const quoteBlotterRequest: IQuoteBlotterRequest = {
      page: page - 1,
      size: pageSize ?? 25,
      sort: sort ?? computedSort,
      startDate: toStartOfDay(new Date(filters.startDate)).toISOString(),
      endDate: toEndOfDay(new Date(filters.endDate)).toISOString(),
      activityScope: filters.activityFilter,
      salesDeskId: filters.deskFilter ?? undefined,
    };
    if (filters.quickSearchTerm) {
      quoteBlotterRequest.fullTextSearch = translateText(
        lang,
        'en',
        filters.quickSearchTerm.trim().length > 1 ? filters.quickSearchTerm.trim() : '',
      );
    }
    if (filters.advancedFilters && filters.currencyFilter !== 'allCurrencies') {
      quoteBlotterRequest.currency = filters.currencyFilter;
    }

    if (filters.productFilter && filters.productFilter !== 'allProducts') {
      quoteBlotterRequest.product = filters.productFilter;
    }

    if (filters.platformFilter && filters.platformFilter !== 'allPlatforms') {
      quoteBlotterRequest.platforms = filters.platformFilter;
    }

    return quoteBlotterRequest;
  },
);

export function getQuoteBlotterPreferenceState(state: QuoteBlotterState) {
  return state.blotterPreferenceState;
}

export function getLoading(state: QuoteBlotterState): boolean {
  return state.loading;
}

export function getIsFetchingFailed(state: QuoteBlotterState): boolean {
  return state.isFetchingFailed;
}

export function areBlotterPreferencesInError(state: AppState): boolean {
  return state.blotters.quotes.areBlotterPreferencesInError;
}

export function canSaveBlotterPreferences(state: AppState): boolean {
  return state.blotters.quotes.canSaveBlotterPreferences;
}

export function getQuoteBlotterAvailablePlatforms(state: AppState) {
  return state.blotters.quotes.availablePlatforms;
}

export function getQuoteBlotterAvailablePlatformsFetchingStatus(state: AppState) {
  return state.blotters.quotes.availablePlatformsFetchingStatus;
}
